/**
 * The router. A `switch` on `location.pathname`, and deliberately nothing more.
 *
 * ADR-0013's no-framework stance is not asceticism — it is that a routing library brings a
 * matcher, a link component, a history abstraction and a context provider to solve a problem
 * this app states in ten paths. What it would actually buy is nested layouts and lazy route
 * splitting, neither of which exists here, against a bundle that has to load over two bars of
 * signal at a ground.
 *
 * ## Why there is a URL at all
 *
 * P0 held the destination in React state and never touched the address bar, which was correct
 * while there were three screens and no way to arrive at one. Two things changed with #34.
 *
 * The **back button** now has an answer. With ten screens a scorer who opens a match from the
 * list and presses back expects the list, and on Android that is the system gesture, not a
 * chrome affordance he can ignore. Without history entries it closed the tab — on a phone
 * carrying an unsynced innings.
 *
 * And a **match detail view is a place**, so it needs an address: `/match/:id` is what makes
 * "look at this one" survive a reload, which is the same reload the whole storage design
 * already promises to survive. #43 adds the tournament addresses on the same reasoning — a
 * league table is the single most shared screen in grassroots cricket.
 *
 * ## What is *not* here
 *
 * No guarding. Whether a destination is reachable is `screenFor`'s decision and stays there —
 * a router that redirected a signed-out visitor would be a second gate, and two gates disagree.
 * This file only answers "which destination does this string name", and the inverse.
 */
import { APP_PATHS } from "@sportsos/engine";
import type { Destination, Screen } from "./screen.ts";

/** A destination, plus the one id any of them carries. */
export interface Route {
  readonly destination: Destination;
  /** The match `/match/:id` names. Null for every other destination. */
  readonly matchId: string | null;
  /** The tournament `/tournament/:id` names. Null otherwise. */
  readonly tournamentId: string | null;
  /** The invite code `/join/:code` names. Null otherwise. */
  readonly inviteCode: string | null;
}

const HOME: Route = { destination: "home", matchId: null, tournamentId: null, inviteCode: null };

/**
 * The match list's address.
 *
 * `/` used to be this, and is not any more: it serves the front door now — a server-rendered
 * marketing document for someone who arrives knowing nothing (ADR-0014). A named constant
 * rather than four literals, because `pathFor` gives this answer for the home destination and
 * again for every detail screen asked for without an id, and those have to agree.
 */
const HOME_PATH = APP_PATHS.home;

const plain = (destination: Destination): Route => ({
  destination,
  matchId: null,
  tournamentId: null,
  inviteCode: null,
});

/**
 * Which destination a pathname names.
 *
 * Unknown paths resolve to the home screen rather than to a 404 screen. A scorer who mistypes
 * a URL, or follows a link from a build that had a route this one does not, should land on his
 * matches — an app whose front door is a dead end is worse than one that quietly goes home.
 */
export function routeFor(pathname: string): Route {
  // Trailing slashes are equivalent, not distinct: `/profile/` and `/profile` are the same
  // place, and a router that disagreed would send one of them to the home screen.
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

  switch (path) {
    case HOME_PATH:
      return HOME;
    // `/` is the front door and is served by the server as a document, so the SPA should
    // never be asked to render it (ADR-0014). If it somehow is — a stale cached bundle, a
    // dev server misconfiguration — the match list is the safe answer, and the same one this
    // function gave before the front door existed.
    case "":
    case "/":
      return HOME;
    case "/setup":
      return plain("setup");
    case "/scoring":
      return plain("scoring");
    case "/profile":
      return plain("profile");
    case "/settings":
      return plain("settings");
    case "/tournaments":
      return plain("tournaments");
    case "/tournament/new":
      // Before the `:id` match below, and this ordering is the whole reason `new` is not a
      // legal tournament id anywhere: a tournament called "new" would otherwise shadow the
      // create screen and there would be no way to reach it.
      return plain("tournament_new");
    case APP_PATHS.signIn:
      return plain("sign_in");
    case APP_PATHS.signUp:
      return plain("sign_up");
    // The spelling this codebase used before the front door existed. Kept working rather than
    // broken: it is in bookmarks and in at least one shared screenshot. `pathFor` answers
    // `sign_in` with the unhyphenated form, so boot's canonicalisation rewrites the address
    // bar and the old spelling quietly stops propagating.
    case "/sign-in":
      return plain("sign_in");
    // The AI teaser (#44). A place of its own rather than a panel on the home screen,
    // because P2's producers land behind this address and a screen that moved once the
    // backend existed would break every link shared in the meantime.
    case "/ai":
      return plain("ai_assistant");
    case "/stats":
      return plain("player_stats");
  }

  const matchId = segmentAfter(path, "/match/");
  // A bare `/match/` names no match. Falling through to home beats opening a detail screen
  // that would immediately report that it could not find the empty string.
  if (matchId !== null) return { destination: "match_detail", matchId, tournamentId: null, inviteCode: null };

  const tournamentId = segmentAfter(path, "/tournament/");
  if (tournamentId !== null) {
    return { destination: "tournament_detail", matchId: null, tournamentId, inviteCode: null };
  }

  const inviteCode = segmentAfter(path, "/join/");
  if (inviteCode !== null) {
    return { destination: "join_team", matchId: null, tournamentId: null, inviteCode };
  }

  return HOME;
}

/**
 * The one path segment between a prefix and an optional suffix, decoded.
 *
 * Null when the segment is empty or itself contains a `/` — both mean this is not the shape
 * of address being asked about, and guessing at a two-segment id is how `/match/a/b` becomes
 * a detail screen for a match that cannot exist.
 */
function segmentAfter(path: string, prefix: string, suffix = ""): string | null {
  if (!path.startsWith(prefix) || !path.endsWith(suffix)) return null;
  const segment = path.slice(prefix.length, path.length - suffix.length);
  if (segment === "" || segment.includes("/")) return null;
  return decodeURIComponent(segment);
}

/** The address for a destination. The exact inverse of `routeFor` for every real route. */
export function pathFor(destination: Destination, id: string | null = null): string {
  switch (destination) {
    case "home":
      return HOME_PATH;
    // Unhyphenated, and matching each other. The front door links to both.
    case "sign_in":
      return APP_PATHS.signIn;
    case "sign_up":
      return APP_PATHS.signUp;
    case "setup":
      return "/setup";
    case "scoring":
      return "/scoring";
    case "profile":
      return "/profile";
    case "settings":
      return "/settings";
    case "tournaments":
      return "/tournaments";
    case "tournament_new":
      return "/tournament/new";
    case "match_detail":
      // Without an id there is no match to show, so this is home — the same answer
      // `routeFor` gives `/match/`, which keeps the two functions honest about each other.
      return id === null ? HOME_PATH : `/match/${encodeURIComponent(id)}`;
    case "tournament_detail":
      return id === null ? "/tournaments" : `/tournament/${encodeURIComponent(id)}`;
    case "ai_assistant":
      return "/ai";
    case "join_team":
      return id === null ? HOME_PATH : `/join/${encodeURIComponent(id)}`;
    case "player_stats":
      return "/stats";
  }
}

/**
 * The address the bar should show, given a route and the screen the gate resolved it to.
 *
 * `null` means leave the address alone — there is nothing to correct yet.
 *
 * ## Why this is a function here rather than four lines in an effect
 *
 * It was four lines in an effect, and it shipped a bug that defeated the front door's primary
 * call to action. `screenFor` answers `"loading"` for every destination on the first commit,
 * before the server has said who this is; the effect read "not one of the two forms" as "a
 * signed-in scorer asked for a form he does not need", rewrote the address to `/matches`, and
 * a Visitor who tapped "Score a match free" was then shown the *sign-in* screen when
 * `GET /api/me` came back 401.
 *
 * Nothing caught it. The browser journey reaches sign-up through the in-form toggle rather
 * than through the address, so the whole e2e suite stayed green — which is the same reason
 * `screenFor` was extracted out of `main.tsx` in the first place: a negative about routing
 * rots silently while it lives in JSX, and here it is a dozen lines and a table of cases.
 *
 * ## The two things it decides
 *
 * **Spelling.** `routeFor` accepts more strings than `pathFor` produces — a trailing slash, an
 * unknown path, and `/` itself, which the server answers with the front door document so the
 * app should never see it (ADR-0014). Left alone, a scorer signs in at whatever string he
 * arrived on and his history learns *that*, so an old `/` bookmark stays `/` for ever and the
 * move off the root never happens for the people who were already here.
 *
 * **Following the gate.** `sign_in` and `sign_up` are the only destinations `screenFor`
 * refuses for a reason that is not a failure: a valid session outranks a request for a form.
 * Leaving `/signup` in the bar then names a screen nobody is looking at, and it is the address
 * the browser offers back next time.
 *
 * This is not a second gate. It decides what the address *says*; what may be reached is
 * `screenFor`'s decision and stays there.
 */
export function addressFor(route: Route, screen: Screen): string | null {
  // Nobody knows who this is yet, so nothing has been decided and there is nothing to
  // correct. The bug above lived in the absence of this line.
  if (screen === "loading") return null;

  const asked = route.destination;
  if ((asked === "sign_in" || asked === "sign_up") && screen !== "sign_in" && screen !== "sign_up") {
    return pathFor("home");
  }
  return pathFor(asked, idOf(route));
}

/** The id a destination's path carries, read back off a route. */
export const idOf = (route: Route): string | null => route.matchId ?? route.tournamentId ?? route.inviteCode;

/** The four tabs matching the CricAI.co design. */
export type Tab = "home" | "team" | "stats" | "account";

/**
 * Which tab is lit for a destination.
 *
 * Setup and scoring map to `home` even though neither renders the bar. That is not a
 * contradiction — it is what makes the tab the scorer left from still be the lit one when a
 * match is abandoned back to the list, rather than the bar flickering to a different tab on
 * the way through.
 *
 * The tournament screens map there too. They are reached *from* the home screen's sections
 * rather than from a tab of their own — a fourth tab was rejected for the reason `TabBar`
 * gives: a section becomes a tab when it is somewhere a scorer goes directly, and in P1 a
 * tournament is somewhere he goes from the card that names it.
 */
export function tabFor(destination: Destination): Tab {
  switch (destination) {
    case "profile":
      return "team";
    case "settings":
      return "account";
    case "tournaments":
    case "tournament_new":
    case "tournament_detail":
      return "stats";
    case "player_stats":
      return "account";
    case "home":
    case "match_detail":
    case "setup":
    case "scoring":
    case "ai_assistant":
    case "join_team":
    // Neither renders the bar — `showsTabBar` says so. They answer here for the same reason
    // setup and scoring do: the tab the scorer left from is still the lit one when he comes
    // back out, rather than the bar flickering to a different tab on the way through.
    case "sign_in":
    case "sign_up":
      return "home";
  }
}
